import { useMemo, useState } from "react";
import { Button, Flex, Input, Space, Table, Tabs, Typography } from "antd";
import {
  useGetPromosQuery,
  useGetPromosByTypeQuery,
} from "../../../api/admin/webshop/webshopAdminApi";
import { getPromoTableColumns } from "./PromoTableColumns";
import { PlusOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import promoTabItems from "./PromoTabItems";
import CreatePromoModalV2 from "./create/CreatePromoModalV2";
import { isUtcDateInPast } from "./promoDateUtils";
import { PromotionItem } from "../../../api/admin/webshop/Promotion";

const { Title, Text } = Typography;

export default function WebshopPromotions() {
  const [activeTab, setActiveTab] = useState("1");
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPromoCode, setSelectedPromoCode] = useState<string | undefined>(
    undefined,
  );

  // RTK Query hooks
  const {
    data: allPromosData,
    isLoading: isAllLoading,
    isFetching: isAllFetching,
    refetch: refetchAllPromos,
  } = useGetPromosQuery();

  const {
    data: privatePromoData,
    isLoading: isPrivateLoading,
    isFetching: isPrivateFetching,
    refetch: refetchPrivatePromos,
  } = useGetPromosByTypeQuery("PROMO#PRIVATE", {
    skip: activeTab !== "2",
  });

  const allItems: PromotionItem[] = useMemo(
    () => allPromosData?.items || [],
    [allPromosData],
  );

  const privateItems: PromotionItem[] = useMemo(
    () => privatePromoData?.items || [],
    [privatePromoData],
  );

  // Active / non-expired promotions (status !== "EXPIRED" and end date not passed)
  const activeItems: PromotionItem[] = useMemo(() => {
    return allItems.filter((item) => {
      if (item.status === "EXPIRED") return false;
      if (!item.noExpiry && item.endAt && isUtcDateInPast(item.endAt)) {
        return false;
      }
      return true;
    });
  }, [allItems]);

  // Expired promotions
  const expiredItems: PromotionItem[] = useMemo(() => {
    return allItems.filter((item) => {
      if (item.status === "EXPIRED") return true;
      if (!item.noExpiry && item.endAt && isUtcDateInPast(item.endAt)) {
        return true;
      }
      return false;
    });
  }, [allItems]);

  // Search filter function
  const filterBySearch = (items: PromotionItem[]) => {
    if (!searchQuery.trim()) return items;
    const q = searchQuery.toLowerCase().trim();
    return items.filter(
      (p) =>
        (p.promoCode && p.promoCode.toLowerCase().includes(q)) ||
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.customerTitle && p.customerTitle.toLowerCase().includes(q)) ||
        (p.internalNotes?.campaign &&
          p.internalNotes.campaign.toLowerCase().includes(q)),
    );
  };

  const filteredActiveItems = useMemo(
    () => filterBySearch(activeItems),
    [activeItems, searchQuery],
  );

  const filteredPrivateItems = useMemo(
    () => filterBySearch(privateItems),
    [privateItems, searchQuery],
  );

  const filteredExpiredItems = useMemo(
    () => filterBySearch(expiredItems),
    [expiredItems, searchQuery],
  );

  const handleEdit = (code: string) => {
    setSelectedPromoCode(code);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedPromoCode(undefined);
    setIsModalOpen(true);
  };

  const handleRefresh = () => {
    refetchAllPromos();
    if (activeTab === "2") {
      refetchPrivatePromos();
    }
  };

  const columns = getPromoTableColumns({ onEdit: handleEdit });

  return (
    <Flex gap={16} vertical style={{ width: "100%", padding: "4px 0" }}>
      {/* Top Header */}
      <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Promotion & Discount Management
          </Title>
          <Text type="secondary">
            Create, schedule, configure discount rules, customer eligibility and
            redemption limits.
          </Text>
        </div>
        <Space wrap>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={isAllFetching || isPrivateFetching}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            style={{
              backgroundColor: "rgb(255, 70, 37)",
              borderColor: "rgb(255, 70, 37)",
            }}
          >
            Create Promotion (V2)
          </Button>
        </Space>
      </Flex>

      {/* Search Input Bar */}
      <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
        <Input
          placeholder="Search discounts by code, title, or campaign..."
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
          style={{ maxWidth: 380 }}
        />
        <Text type="secondary">
          Total Discounts: {allItems.length}
        </Text>
      </Flex>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={promoTabItems({
          activeCount: activeItems.length,
          privateCount: privateItems.length,
          expiredCount: expiredItems.length,
          activePromoTable: (
            <Table
              rowKey={(record) => record.promoCode}
              dataSource={filteredActiveItems}
              loading={isAllLoading}
              columns={columns}
              size="small"
              pagination={{
                pageSize: 15,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
              style={{
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                borderRadius: "8px",
                overflow: "hidden",
                backgroundColor: "#fff",
              }}
            />
          ),
          privatePromoTable: (
            <Table
              rowKey={(record) => record.promoCode}
              dataSource={filteredPrivateItems}
              loading={isPrivateLoading}
              columns={columns}
              size="small"
              pagination={{
                pageSize: 15,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
              style={{
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                borderRadius: "8px",
                overflow: "hidden",
                backgroundColor: "#fff",
              }}
            />
          ),
          expiredPromoTable: (
            <Table
              rowKey={(record) => record.promoCode}
              dataSource={filteredExpiredItems}
              loading={isAllLoading}
              columns={columns}
              size="small"
              pagination={{
                pageSize: 15,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
              style={{
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                borderRadius: "8px",
                overflow: "hidden",
                backgroundColor: "#fff",
              }}
            />
          ),
        })}
      />

      {/* V2 Promotion Modal */}
      <CreatePromoModalV2
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        promoCode={selectedPromoCode}
        onSuccess={handleRefresh}
      />
    </Flex>
  );
}
